import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Database, Download, RefreshCw } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const DatabaseViewer = () => {
  const { token, user } = useContext(AuthContext);
  const [collections, setCollections] = useState({});
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionData, setCollectionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "school_admin") {
      toast.error("Access denied: Admin only");
      return;
    }
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/admin/database/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCollections(response.data);
    } catch (error) {
      toast.error("Failed to load collections");
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionData = async (collection) => {
    try {
      const response = await axios.get(`${API}/admin/database/${collection}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCollectionData(response.data);
      setSelectedCollection(collection);
    } catch (error) {
      toast.error(`Failed to load ${collection}`);
    }
  };

  const exportDatabase = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/database/export`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Download as JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `skilledge_db_export_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      toast.success("Database exported successfully!");
    } catch (error) {
      toast.error("Failed to export database");
    }
  };

  if (user?.role !== "admin" && user?.role !== "school_admin") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Card>
            <CardContent className="p-12 text-center">
              <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
              <p className="text-gray-600">You don't have permission to view this page.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="database-viewer">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <Database className="w-10 h-10 text-indigo-500" />
              Database Viewer
            </h1>
            <p className="text-gray-600 text-lg">View and manage database collections</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchCollections} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={exportDatabase}>
              <Download className="w-4 h-4 mr-2" />
              Export All
            </Button>
          </div>
        </div>

        {/* Collections Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Collections Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Object.entries(collections).map(([collection, count]) => (
                <Card
                  key={collection}
                  className="cursor-pointer card-hover"
                  onClick={() => fetchCollectionData(collection)}
                >
                  <CardContent className="p-4 text-center">
                    <Database className="w-8 h-8 mx-auto mb-2 text-indigo-500" />
                    <h3 className="font-semibold text-sm mb-1">{collection}</h3>
                    <p className="text-2xl font-bold text-indigo-600">{count}</p>
                    <p className="text-xs text-gray-500">documents</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Collection Data */}
        {collectionData && (
          <Card>
            <CardHeader>
              <CardTitle>
                {selectedCollection} ({collectionData.total} documents)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                {collectionData.data.length > 0 ? (
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto max-h-[600px]">
                    <pre className="text-xs">
                      {JSON.stringify(collectionData.data, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Database className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No documents in this collection</p>
                  </div>
                )}
              </div>
              {collectionData.total > collectionData.limit && (
                <div className="mt-4 text-sm text-gray-600 text-center">
                  Showing {collectionData.skip + 1} - {Math.min(collectionData.skip + collectionData.limit, collectionData.total)} of {collectionData.total}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default DatabaseViewer;
